import { makeStyles } from '@material-ui/core';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import { StatusType } from '../../enum';
import { Typography } from '../../../typography';
import { styledComponent } from '../../../../hoc';
import { TypographyProps } from '@mui/material';

/**
 * `StatusTable` is a styled version of the MUI `Table` component.
 *
 * - Removes box-shadow and background color for a cleaner look.
 * - Customizes padding for the first and last cells in each row.
 *
 * @category InformationCard
 */
export const StatusTable = styledComponent(Table)(() => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',

  '& tr td:first-of-type': {
    paddingLeft: 0,
  },

  '& tr td:last-of-type': {
    paddingRight: 0,
  },
}));

/**
 * `StatusCell` is a styled version of the MUI `TableCell` component.
 *
 * - Adds consistent padding and removes the bottom border.
 *
 * @category InformationCard
 */
export const StatusCell = styledComponent(TableCell)(() => ({
  padding: '8px 16px',
  borderBottom: 'none',
}));

/**
 * `StatusContent` is a styled `div` that aligns content in a horizontal row.
 *
 * @remarks
 * - Content is aligned vertically centered with `alignItems: center`.
 * - Adds spacing between elements using `columnGap`.
 *
 * @param {Theme} theme - The MUI theme object for consistent theming.
 *
 * @category InformationCard
 */
export const StatusContent = styledComponent('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
  color: theme.palette.text.primary,
}));

/**
 * `LinkCell` is a styled version of the `Typography` component.
 *
 * - Changes text color to the primary theme color.
 * - Adds pointer cursor to indicate interactivity.
 *
 * @param {TypographyProps} props - The `Typography` props to customize typography behavior.
 *
 * @category InformationCard
 */
export const LinkCell = styledComponent<TypographyProps>(Typography)(
  ({ theme }) => ({
    color: theme.palette.primary.main,
    cursor: 'pointer',
  }),
);

/**
 * `useStatusCarsStyles` provides dynamic styles for status types.
 *
 * - Styles are applied based on the `StatusType` enum.
 * - Each status type (`ok`, `error`, `running`, etc.) maps to a corresponding theme color.
 *
 * @param {Theme} theme - The MUI theme object for accessing `palette` properties.
 *
 * @example
 * ```tsx
 * const classes = useStatusCarsStyles();
 * <div className={classes[StatusType.ok]}>Status OK</div>
 * ```
 *
 * @category InformationCard
 */
export const useStatusCarsStyles = makeStyles(theme => ({
  /** Style for cells with `StatusType.ok` status */
  [StatusType.ok]: {
    color: theme.palette.status.ok,
  },

  /** Style for cells with `StatusType.error` status */
  [StatusType.error]: {
    color: theme.palette.status.error,
  },

  /** Style for cells with `StatusType.running` status */
  [StatusType.running]: {
    color: theme.palette.status.running,
  },

  /** Style for cells with `StatusType.warning` status */
  [StatusType.warning]: {
    color: theme.palette.status.warning,
  },

  /** Style for cells with `StatusType.pending` status */
  [StatusType.pending]: {
    color: theme.palette.status.pending,
  },

  /** Style for cells with `StatusType.aborted` status */
  [StatusType.aborted]: {
    color: theme.palette.status.aborted,
  },
}));
